import type { Channel, Message } from '@novachat/shared';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Paperclip, Search, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { create } from 'zustand';
import { client } from '../lib/client';
import { useDirectory } from '../lib/directory';
import { errorMessage } from '../lib/errors';
import { plainText } from '../lib/mentions';
import { clock, dayLabel } from '../lib/time';
import { jumpToMessage } from '../state/chat';
import { useLive } from '../state/live';
import { Avatar } from './Avatar';
import { useChannels } from './Sidebar';
import { closeOnBackdrop } from '../lib/dismiss';

/** Opened from the sidebar or with ⌘K / Ctrl+K; rendered once by Shell. */
export const useSearch = create<{ open: boolean; opened: number; show(): void; hide(): void }>()((set) => ({
  open: false,
  /** Bumped on every show: a stale "open" (closed window, close event not yet handled) can never swallow it. */
  opened: 0,
  show: () => set((s) => ({ open: true, opened: s.opened + 1 })),
  hide: () => set({ open: false }),
}));

export const SEARCH_KEY = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K';

export function SearchDialog() {
  const open = useSearch((s) => s.open);
  const opened = useSearch((s) => s.opened);
  return open ? <Dialog key={opened} /> : null;
}

const channelTitle = (channel: Channel | undefined) => (!channel ? 'Kanal' : channel.kind === 'dm' ? (channel.peer?.name ?? 'Direkt mesaj') : `#${channel.name}`);

/** Marks every searched word in a snippet (Turkish-aware, case-insensitive). */
function highlight(text: string, q: string): ReactNode {
  const words = q.toLocaleLowerCase('tr').split(/\s+/).filter((w) => w.length > 1);
  if (words.length === 0) return text;
  const lower = text.toLocaleLowerCase('tr');
  const parts: ReactNode[] = [];
  let at = 0;
  while (at < text.length) {
    const hits = words.map((w) => ({ w, i: lower.indexOf(w, at) })).filter((h) => h.i >= 0);
    if (hits.length === 0) break;
    const next = hits.reduce((a, b) => (b.i < a.i ? b : a));
    parts.push(text.slice(at, next.i), <mark key={next.i} className="rounded-[3px] bg-brand-400/30 px-px text-fg-1">{text.slice(next.i, next.i + next.w.length)}</mark>);
    at = next.i + next.w.length;
  }
  parts.push(text.slice(at));
  return parts;
}

function Dialog() {
  const dialog = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const people = useDirectory();
  const { data: channels = [] } = useChannels();
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [where, setWhere] = useState('');
  const [has, setHas] = useState<'' | 'file' | 'image'>('');

  useEffect(() => {
    dialog.current?.showModal();
    const frame = requestAnimationFrame(() => input.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);
  // Search as you type, a moment after the last key.
  useEffect(() => {
    const timer = setTimeout(() => setQ(text.trim()), 300);
    return () => clearTimeout(timer);
  }, [text]);

  const filters = { ...(q ? { q } : {}), ...(from ? { from } : {}), ...(where ? { in: where } : {}), ...(has ? { has } : {}) };
  const active = Object.keys(filters).length > 0;
  const results = useInfiniteQuery({
    queryKey: ['search', filters],
    queryFn: ({ pageParam }) => client.api.search({ ...filters, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: '',
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: active,
    staleTime: 30_000,
  });
  const found = results.data?.pages.flatMap((page) => page.messages) ?? [];

  function open(message: Message) {
    useLive.getState().select(message.channelId);
    jumpToMessage(message.channelId, message.id);
    dialog.current?.close();
  }

  const select = 'h-9 min-w-0 flex-1 rounded-md border border-line-strong bg-surface-2 px-2 text-base text-fg-1 sm:flex-none sm:text-[13px]';
  return (
    <dialog onClick={closeOnBackdrop}
      ref={dialog}
      onClose={() => useSearch.getState().hide()}
      aria-label="Ara"
      className="m-auto mt-[8dvh] flex max-h-[80dvh] w-[min(640px,calc(100vw-24px))] flex-col rounded-xl border border-line-strong bg-surface-1 p-0 text-fg-1 backdrop:bg-black/60 [&:not([open])]:hidden"
    >
      <div className="flex items-center gap-2 border-b border-line px-4 py-3">
        <Search size={18} className="flex-none text-fg-3" aria-hidden="true" />
        <input
          ref={input}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Mesajlarda ara"
          aria-label="Mesajlarda ara"
          className="min-w-0 flex-1 border-0 bg-transparent text-base text-fg-1 outline-none placeholder:text-fg-3"
        />
        <button type="button" aria-label="Kapat" onClick={() => dialog.current?.close()} className="grid size-8 place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-2">
          <X size={18} />
        </button>
      </div>
      <div className="flex flex-wrap gap-2 border-b border-line px-4 py-2.5">
        <select aria-label="Kimden" value={from} onChange={(e) => setFrom(e.target.value)} className={select}>
          <option value="">Herkesten</option>
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select aria-label="Nerede" value={where} onChange={(e) => setWhere(e.target.value)} className={select}>
          <option value="">Her yerde</option>
          {channels.map((c) => (
            <option key={c.id} value={c.id}>
              {channelTitle(c)}
            </option>
          ))}
        </select>
        <select aria-label="Tür" value={has} onChange={(e) => setHas(e.target.value as typeof has)} className={select}>
          <option value="">Tüm mesajlar</option>
          <option value="file">Dosyalı</option>
          <option value="image">Görselli</option>
        </select>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {!active && <p className="m-0 p-3 text-sm text-fg-3">Bir kelime yaz ya da filtre seç. “kitap” yazınca “kitaplar”ı da bulur.</p>}
        {active && results.isPending && <p className="m-0 p-3 text-sm text-fg-3">Aranıyor…</p>}
        {active && results.isError && <p className="m-0 p-3 text-sm text-[#EE9191]">{errorMessage(results.error)}</p>}
        {active && results.isSuccess && found.length === 0 && <p className="m-0 p-3 text-sm text-fg-3">Sonuç yok.</p>}
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
          {found.map((message) => {
            const author = message.author ?? { id: 'unknown', name: 'Silinmiş kullanıcı' };
            const snippet = plainText(message.content, people).replace(/\s+/g, ' ').slice(0, 220);
            return (
              <li key={message.id}>
                <button type="button" onClick={() => open(message)} className="flex w-full gap-3 rounded-md border-0 bg-transparent px-3 py-2.5 text-left hover:bg-surface-2">
                  <Avatar id={author.id} name={author.name} size={32} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline gap-2 text-xs text-fg-3">
                      <span className="truncate text-sm font-semibold text-fg-1">{author.name}</span>
                      <span className="truncate">{channelTitle(channels.find((c) => c.id === message.channelId))}</span>
                      <span className="ml-auto flex-none">
                        {dayLabel(message.createdAt)} {clock(message.createdAt)}
                      </span>
                    </span>
                    {snippet && <span className="mt-0.5 block text-sm break-words text-fg-body">{highlight(snippet, q)}</span>}
                    {message.attachments.length > 0 && (
                      <span className="mt-0.5 flex items-center gap-1 text-xs text-fg-3">
                        <Paperclip size={12} aria-hidden="true" />
                        {message.attachments.map((a) => a.fileName).join(', ')}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {results.hasNextPage && (
          <button
            type="button"
            disabled={results.isFetchingNextPage}
            onClick={() => void results.fetchNextPage()}
            className="nc-press mx-auto my-2 block h-9 rounded-md border border-line-strong bg-surface-2 px-4 text-sm font-semibold"
          >
            {results.isFetchingNextPage ? 'Yükleniyor…' : 'Daha fazla'}
          </button>
        )}
      </div>
    </dialog>
  );
}
